'use client';
import { useState } from 'react';
import useSWR from 'swr';
import { useLocale } from '@/components/common/locale-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslations } from '@/hooks/use-translations';
import { countrySelectOptions } from '@/lib/auth/iso-countries';
import { AU_PASS_CODE } from '@/lib/enterprise-pass/policy';
import { CountryCombobox } from './country-combobox';

export function EnterprisePassForm({
  isSignedIn = false,
  onPrepared,
}: {
  isSignedIn?: boolean;
  onPrepared?: () => Promise<boolean>;
}) {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const [companyName, setCompanyName] = useState('');
  const [code, setCode] = useState(AU_PASS_CODE);
  const [jurisdiction, setJurisdiction] = useState('US');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { data, error: loadError } = useSWR(
    '/api/enterprise-pass',
    async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Unavailable');
      return response.json();
    },
  );
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
          const response = await fetch('/api/enterprise-pass', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyName, code, jurisdiction }),
          });
          const result = await response.json();
          if (!response.ok) {
            setError(result.error || t('enterprisePass.failed'));
            return;
          }
          if (onPrepared) await onPrepared();
          else {
            document.cookie = 'arkyv_event_signup=; Max-Age=0; Path=/';
            window.location.assign('/');
          }
        } catch {
          setError(t('enterprisePass.failed'));
        } finally {
          setIsSubmitting(false);
        }
      }}
    >
      <div>
        <h1 className="text-2xl font-semibold">{t('enterprisePass.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('enterprisePass.description')}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="pass-company">{t('enterprisePass.company')}</Label>
        <Input
          id="pass-company"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          maxLength={128}
          required
          autoComplete="organization"
        />
      </div>
      {isSignedIn && (
        <CountryCombobox
          options={countrySelectOptions}
          value={jurisdiction}
          id="pass-country"
          onValueChange={setJurisdiction}
        />
      )}
      <div className="space-y-2">
        <Label htmlFor="pass-code">{t('enterprisePass.code')}</Label>
        <Input
          id="pass-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={64}
          required
        />
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {(loadError || data?.isAvailable === false) && (
        <p role="status" className="text-sm text-muted-foreground">
          {t('enterprisePass.unavailable')}
        </p>
      )}
      <Button type="submit" disabled={isSubmitting || !data?.isAvailable}>
        {isSubmitting ? t('common.loading') : t('enterprisePass.activate')}
      </Button>
      <p className="text-xs text-muted-foreground">
        {t('enterprisePass.expiry')}
      </p>
    </form>
  );
}
